import { ARENA } from '../../config/constants.js';

const APPROACH_TRIGGER_RANGE = 140; // starts telegraphing once this close to the player
const TELEGRAPH_DURATION = 0.6;
const CHARGE_SPEED_MULTIPLIER = 3.5; // relative to its own moveSpeed, only while actually charging
const CHARGE_DURATION = 0.8; // charge direction is locked in at telegraph time, so a dodge sends it straight past — cap how long it commits to that miss before re-engaging, instead of sailing off in a straight line forever
const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08; // brief white flinch flash on taking damage

/**
 * A minion that closes in normally, then telegraphs (brief pause) and
 * dashes in a straight line toward wherever the player was standing when
 * the telegraph started — heavy contact damage, no ranged attack. Same
 * public shape as Minion (x/y/radius/hp/isDead/takeDamage/update) so it
 * drops into CombatSystem's enemy registry and MinionWaveSystem's pool
 * without either needing to know the difference.
 */
export class Charger {
  constructor(eventBus, { x, y, radius = 6, hp, moveSpeed, contactDamage }) {
    this.eventBus = eventBus;
    this.type = 'charger';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.isDead = false;

    this._state = 'approach'; // approach -> telegraph -> charging -> (back to) approach
    this._telegraphTimer = 0;
    this._chargeDir = { x: 0, y: 0 };
    this._chargeTimer = 0;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  /** Read by Game.js to flash the sprite during the telegraph window. */
  get isTelegraphing() {
    return this._state === 'telegraph';
  }

  update(dt, player, _bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;

    if (this._state === 'approach') {
      if (dist < APPROACH_TRIGGER_RANGE) {
        this._state = 'telegraph';
        this._telegraphTimer = TELEGRAPH_DURATION;
        this._chargeDir = { x: dx / dist, y: dy / dist }; // locked in now, not re-aimed once charging
      } else {
        this.x += (dx / dist) * this.moveSpeed * dt;
        this.y += (dy / dist) * this.moveSpeed * dt;
      }
    } else if (this._state === 'telegraph') {
      this._telegraphTimer -= dt;
      if (this._telegraphTimer <= 0) {
        this._state = 'charging';
        this._chargeTimer = CHARGE_DURATION;
      }
    } else {
      this.x += this._chargeDir.x * this.moveSpeed * CHARGE_SPEED_MULTIPLIER * dt;
      this.y += this._chargeDir.y * this.moveSpeed * CHARGE_SPEED_MULTIPLIER * dt;
      this._chargeTimer -= dt;
      if (this._chargeTimer <= 0) this._state = 'approach'; // missed — re-engage instead of charging off the map forever
    }

    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  // Defense-in-depth alongside the charge-duration cap above — no minion
  // should ever be able to wander outside the fightable area and become
  // unreachable, softlocking the wave (see MinionWaveSystem.isStageCleared).
  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  /** Same shape as Minion.takeDamage — used by CombatSystem's shared enemy collision loop. */
  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    // Emit the clamped HP delta, not the raw incoming hit — see
    // Boss.takeDamage's identical fix, same overkill-XP issue here on a
    // one-shot kill.
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
